import { useEffect, useRef, useState } from "react";
import { DeltaPreview } from "./recapture-dialog";
import type { Delta } from "./recapture-dialog";
import { MATCH_KINDS } from "@app/lib/match-kinds";

// Merge two same-target rules into one. The suggested pattern is the longest
// common prefix of the two rules (a PREFIX rule); it's shown EDITABLE at the top
// and every edit re-previews which txns the merged rule newly captures (entering)
// and which would leave the combined coverage (leaving) — debounced. The parent
// owns the preview fetch and the mergeRules mutation.
export default function MergeRuleDialog({
  initialPattern,
  initialMatchKind,
  delta,
  previewing,
  saving,
  onPreview,
  onMerge,
  onCancel,
}: {
  initialPattern: string;
  initialMatchKind: string;
  delta: Delta | null;
  previewing: boolean;
  saving: boolean;
  onPreview: (regex: string, matchKind: string) => void;
  onMerge: (regex: string, matchKind: string, overrideTxnIds: string[], excludeTxnIds: string[]) => void;
  onCancel: () => void;
}) {
  const [pattern, setPattern] = useState(initialPattern);
  const [matchKind, setMatchKind] = useState(initialMatchKind);
  const [overrideManual, setOverrideManual] = useState(false);
  // The over-captured txns (matched by neither original rule). Default to keeping
  // them OUT of the merged rule so attribution stays exact (R16); the operator can
  // opt to capture them by unchecking.
  const [excludeOvercaptured, setExcludeOvercaptured] = useState(true);
  const overcaptured = delta?.captured ?? [];

  // Re-seed when the suggestion arrives (parent fetches it after open).
  useEffect(() => setPattern(initialPattern), [initialPattern]);
  useEffect(() => setMatchKind(initialMatchKind), [initialMatchKind]);

  // Debounced live preview as the merged pattern is edited. The parent seeds the
  // initial delta on open, so an empty pattern (the brief pre-seed state) is
  // skipped rather than previewing nothing.
  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;
  useEffect(() => {
    if (pattern.trim() === "") return;
    const timer = setTimeout(() => onPreviewRef.current(pattern, matchKind), 350);
    return () => clearTimeout(timer);
  }, [pattern, matchKind]);

  const merge = () => {
    const overrideTxnIds =
      overrideManual && delta && delta.droppedManual.length > 0
        ? delta.droppedManual.map((r) => r.txn.id)
        : [];
    const excludeTxnIds =
      excludeOvercaptured && overcaptured.length > 0
        ? overcaptured.map((r) => r.txn.id)
        : [];
    onMerge(pattern, matchKind, overrideTxnIds, excludeTxnIds);
  };

  const isRegex = matchKind === "REGEX";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[680px] max-h-[88vh] overflow-y-auto p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-semibold text-gray-800">Merge into one rule</h2>
        <p className="text-xs text-gray-500">
          The two selected rules become a single rule matching this pattern. Edit it
          to capture more or fewer transactions — a shorter prefix captures more.
        </p>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <select
              value={matchKind}
              onChange={(e) => setMatchKind(e.target.value)}
              className="rounded border px-2 py-1.5 text-sm bg-white shrink-0"
            >
              {MATCH_KINDS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <input
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder={isRegex ? "^ACME.*" : "ACME"}
              className="flex-1 min-w-0 rounded border px-2 py-1.5 font-mono text-xs"
            />
          </div>
          <p className="text-[11px] text-gray-400">
            {MATCH_KINDS.find((m) => m.value === matchKind)?.hint}
          </p>
        </div>

        <div className="border-t pt-3">
          <p className="text-xs text-gray-500 uppercase mb-2">Impact</p>
          <DeltaPreview
            delta={delta}
            loading={previewing}
            overrideManual={overrideManual}
            onToggleOverride={setOverrideManual}
          />
        </div>

        {overcaptured.length > 0 && (
          <label className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
            <input
              type="checkbox"
              checked={excludeOvercaptured}
              onChange={(e) => setExcludeOvercaptured(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              This pattern also captures{" "}
              <span className="font-semibold">{overcaptured.length.toLocaleString()}</span>{" "}
              transaction{overcaptured.length === 1 ? "" : "s"} that neither original rule
              matched. Keep {overcaptured.length === 1 ? "it" : "them"} out of the merged
              rule so attribution stays exact{" "}
              <span className="text-amber-700">
                (uncheck to attribute {overcaptured.length === 1 ? "it" : "them"} too)
              </span>
              .
            </span>
          </label>
        )}

        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            disabled={saving || previewing || pattern.trim() === ""}
            onClick={merge}
            className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
          >
            {saving ? "Merging…" : "Merge rules"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
